import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withStyles, makeStyles, createStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import { Typography } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CloseIcon from "@material-ui/icons/Close";
import {
  setFilterDownload,
  setCurrentPageNumber,
} from "../../actions";

const useStyles = makeStyles((theme) =>
  createStyles({
    container: {
      width: 500,
      height: "100vh",
      padding: 16,
      boxSizing: "border-box",
      position: "relative",
      display: "flex",
      flexDirection: "column",
    },
    filter: {
      marginLeft: theme.spacing(2),
      paddingTop: theme.spacing(3),
    },
    filterItem: {
      marginTop: theme.spacing(4),
    },
    filterButtonContainer: {
      position: "absolute",
      bottom: 45,
      left: "50%",
      transform: "translateX(-50%)",
      width: "100%",
      display: "flex",
      justifyContent: "space-around",
    },
    formControl: {
      margin: theme.spacing(1),
      width: "89%",
    },
    dataBtn: {
      minWidth: 200,
      height: theme.spacing(4),
      paddingTop: theme.spacing(1.5),
    },
    tagSelect: {
      marginTop: theme.spacing(1),
      marginLeft: theme.spacing(1),
      fontSize: 16,
      minWidth: 416,
      maxWidth: 416,
      "& .MuiInputLabel-root": {
        fontSize: "16px", // Smaller font size for the label (placeholder)
      },
      "& .MuiInputBase-input": {
        fontSize: "16px", // Customize input text size (if needed)
      },
    },
    scrollArea: {
      flexGrow: 1,
      overflowY: "auto",
      paddingRight: 8,
      paddingBottom: 16,
      marginBottom: 80,
    },
    header: {
      display: "flex",
      alignItems: "center",
      padding: "8px 0",
    },
    closeButton: {
      position: "absolute",
      top: 10,
      left: 8,
      zIndex: 10,
    },
    titleText: {
      fontSize: "1rem",
      fontWeight: "bold",
      marginLeft: 42,
    },
  })
);

const normalizeAreaValue = (value) => {
  if (typeof value !== "string") return "";
  return value.replace(/（\d+件）$/, "").trim();
};

const normalizeSelectedAreas = (value) => {
  if (Array.isArray(value)) return value.map(normalizeAreaValue).filter(Boolean);
  if (typeof value === "string") return [normalizeAreaValue(value)].filter(Boolean);
  return [];
};

const FilterDownload = ({
  currentPage,
  toggleDrawer,
  anchor,
  setFilterDownload,
  filterDownload,
  setCurrentPageNumber,
  downloadAreaOptions,
}) => {
  const classes = useStyles();
  const [selectedAreas, setSelectedAreas] = useState([]);

  useEffect(() => {
    if (filterDownload && filterDownload.areaGroup) {
      setSelectedAreas(normalizeSelectedAreas(filterDownload.areaGroup.value));
    }
  }, [filterDownload]);

  const handleAreaGroupChange = (event, newOptions) => {
    // newOptions は { value: "千葉", label: "千葉（2件）", count: 2 } の配列
    const selectedValues = newOptions
      .map((opt) => normalizeAreaValue(opt.value || opt.label || ""))
      .filter(Boolean); // ["千葉", "成田", ...]

    const copyFilter = {
      ...filterDownload,
      areaGroup: {
        key: selectedValues.length === 0 ? "all" : "custom",
        value: selectedValues, // 県域名の配列
      },
    };

    setFilterDownload(copyFilter);
    setSelectedAreas(selectedValues);
  };

  const fetchData = () => {
    setCurrentPageNumber(1);
    toggleDrawer(anchor, false)();
  };

  const handleReset = () => {
    setFilterDownload({
      type: "occurred_datetime",
      order: "desc",
      tag: [],
      assetType: "all",
      areaGroup: {
        key: "all",
        value: [],
      },
    });
    setSelectedAreas([]);
    toggleDrawer(anchor, false)();
    setCurrentPageNumber(1);
  };

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <IconButton
          className={classes.closeButton}
          color="inherit"
          aria-label="open drawer"
          onClick={toggleDrawer(anchor, false)}
          disableRipple={true}
          disableFocusRipple={true}
        >
          <CloseIcon />
        </IconButton>
        <Typography variant="h6" className={classes.titleText}>
          絞り込み条件設定
        </Typography>
      </div>
      <div className={classes.scrollArea}>
        <Grid
          container
          direction="column"
          justifyContent={"flex-start"}
          className={classes.filter}
        >
         <Grid item className={classes.filterItem}>
           <Autocomplete
             multiple
             id="filter-area-group"
             options={(downloadAreaOptions || []).filter(
               (opt) => opt.value !== "all" // 「全て」は Autocomplete からは外す
             )}
             getOptionLabel={(option) => option.label}
             value={(downloadAreaOptions || []).filter((opt) =>
               selectedAreas.includes(normalizeAreaValue(opt.value))
             )}
             onChange={handleAreaGroupChange}
             className={classes.tagSelect}
             renderInput={(params) => (
               <TextField
                 {...params}
                 variant="outlined"
                 label="県域"
                 placeholder={selectedAreas.length === 0 ? "全て" : ""}
               />
             )}
           />
         </Grid>

          <div className={classes.filterButtonContainer}>
            <Button
              className={classes.dataBtn}
              variant="contained"
              color="primary"
              onClick={fetchData}
              disabled={false}
            >
              絞り込む
            </Button>
            <Button
              className={classes.dataBtn}
              variant="contained"
              color="default"
              onClick={handleReset}
              disabled={false}
            >
              リセット
            </Button>
          </div>
        </Grid>
      </div>
    </div>
  );
};

FilterDownload.prototypes = {
  classes: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  currentPage: state.app.currentPage,
  filterDownload: state.download.filterDownload,
  downloadAreaOptions: state.download.areaGroup || [],
});

const mapDispatchToProps = (dispatch) => ({
  setFilterDownload: (param) => dispatch(setFilterDownload(param)),
  setCurrentPageNumber: (param) => dispatch(setCurrentPageNumber(param)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(null, { withTheme: true })(FilterDownload));
